import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'

import { toast } from '@/components/feedback/toast'
import { normalizeApiError } from '@/lib/apiEnvelope'

import {
  getCurrentSubscription,
  getPushSupport,
  subscribeToPush,
  unsubscribeFromPush,
} from '../services/pushService'

export type PushStatus =
  'unsupported' | 'ios-install-required' | 'loading' | 'denied' | 'disabled' | 'enabled'

/** Estado e ações de Web Push deste dispositivo, para a tela de perfil. */
export function usePushNotifications() {
  const [support] = useState(getPushSupport)
  const [permission, setPermission] = useState<NotificationPermission | null>(() =>
    support === 'supported' ? Notification.permission : null,
  )
  const [subscribed, setSubscribed] = useState<boolean | null>(null)

  const refresh = useCallback(async () => {
    if (support !== 'supported') return
    const subscription = await getCurrentSubscription()
    setPermission(Notification.permission)
    setSubscribed(!!subscription)
  }, [support])

  // A permissão pode mudar nas configurações do navegador enquanto o app está aberto.
  useEffect(() => {
    void refresh()
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [refresh])

  const subscribeMutation = useMutation({
    mutationFn: subscribeToPush,
    onSuccess: () => toast.success('Notificações ativadas neste dispositivo.'),
    onError: (error) => toast.error(normalizeApiError(error).detail),
    onSettled: () => void refresh(),
  })

  const unsubscribeMutation = useMutation({
    mutationFn: unsubscribeFromPush,
    onSuccess: () => toast.success('Notificações desativadas neste dispositivo.'),
    onError: (error) => toast.error(normalizeApiError(error).detail),
    onSettled: () => void refresh(),
  })

  /** Deve ser chamado direto do clique: o Safari só aceita o pedido de permissão num gesto do usuário. */
  const enable = async () => {
    const result = await Notification.requestPermission()
    setPermission(result)
    if (result === 'granted') subscribeMutation.mutate()
  }

  const disable = () => unsubscribeMutation.mutate()

  let status: PushStatus
  if (support !== 'supported') status = support
  else if (permission === 'denied') status = 'denied'
  else if (subscribed === null) status = 'loading'
  else status = subscribed && permission === 'granted' ? 'enabled' : 'disabled'

  return {
    status,
    enable,
    disable,
    isPending: subscribeMutation.isPending || unsubscribeMutation.isPending,
  }
}
